import type { CleaningRangeSummaryResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { CleaningOperationalTaskChip } from './CleaningOperationalTaskChip';
import { dayLabel } from './cleaningCalendarHelpers';
import type { OperationalApartmentRow } from './cleaningOperationalRows';

interface CleaningDayAgendaProps {
  rows: OperationalApartmentRow[];
  weekDates: string[];
  selectedDay: string;
  onSelectDay: (date: string) => void;
  todayIso: string;
  selectedId?: string | null;
  onSelect?: (task: CleaningRangeSummaryResponse, trigger: HTMLElement) => void;
}

/** Schmale Fläche: Tagesleiste der Woche und darunter die Apartments des gewählten Tages. */
export function CleaningDayAgenda({
  rows,
  weekDates,
  selectedDay,
  onSelectDay,
  todayIso,
  selectedId,
  onSelect,
}: CleaningDayAgendaProps) {
  const dayRows = rows
    .map((row) => ({ row, tasks: row.byDate.get(selectedDay) ?? [] }))
    .filter((entry) => entry.tasks.length > 0);

  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label="Tag der Woche" className="grid grid-cols-7 gap-0.5">
        {weekDates.map((date) => {
          const { weekday, day } = dayLabel(date);
          const selected = date === selectedDay;
          const count = rows.reduce((n, row) => n + (row.byDate.get(date)?.length ?? 0), 0);
          return (
            <button
              key={date}
              type="button"
              aria-pressed={selected}
              aria-label={`${weekday} ${day}., ${count} ${count === 1 ? 'Reinigung' : 'Reinigungen'}`}
              onClick={() => onSelectDay(date)}
              className={cn(
                'rounded-control flex min-h-14 flex-col items-center justify-center border',
                selected
                  ? 'bg-forest border-forest text-white'
                  : 'border-hairline text-ink bg-white',
                !selected && date === todayIso && 'border-forest',
              )}
            >
              <span className="text-[10px] font-semibold uppercase">{weekday}</span>
              <span className="font-serif text-base font-bold">{day}</span>
              <span
                aria-hidden
                className={cn(
                  'size-1.5 rounded-full',
                  count > 0 ? (selected ? 'bg-mint' : 'bg-forest') : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>

      {dayRows.length === 0 ? (
        <p className="border-hairline rounded-tile text-ink-soft border bg-white p-5 text-sm">
          An diesem Tag keine Reinigungen.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {dayRows.map(({ row, tasks }) => (
            <li
              key={row.propertyId}
              className="border-hairline rounded-tile flex flex-col gap-2 border bg-white p-3"
            >
              <b className="font-serif text-lg">{row.name}</b>
              {tasks.map((task) => (
                <CleaningOperationalTaskChip
                  key={task.id}
                  task={task}
                  selected={task.id === selectedId}
                  onSelect={onSelect}
                />
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
